import S from 's-js';
import { lift } from 's-array';
import { IAjax } from 'lib/Ajax';
import { TZUtils } from 'lib/tzutils';

import { CoordinationCard } from 'data/models/CoordinationCard';
import { CoordinationCardStatus } from 'data/models/CoordinationCardStatus';
import { CoordinationCardStatuses } from 'data/models/CoordinationCardStatuses';
import { CoordinationCardStatusUpdateRequest } from 'data/models/CoordinationCardStatusUpdateRequest';
import { Event } from 'data/models/Event';
import { EventType } from 'data/models/EventType';
import { IssueSeverity } from 'server/enums/Issue';

import { PCCache } from 'data/PCCache';

import { Entrypoints } from 'core/app/entrypoints';

export interface CoordinationCardDetailsStatusTabParams {
    CoordinationCard : CoordinationCard;
    DismissDetails : () => void;
    HasEditStatus : () => boolean;
}

export type CoordinationCardDetailsStatusTab = ReturnType<typeof CoordinationCardDetailsStatusTab>;
export function CoordinationCardDetailsStatusTab(
    tzutils : TZUtils,
    data : PCCache,
    ajax : IAjax,
    entrypoints : Entrypoints,
    {
        CoordinationCard,
        DismissDetails,
        HasEditStatus
    } : CoordinationCardDetailsStatusTabParams
) {
    const
        // NOTE: Here's a spot where we bypass the alt label if the current status is pending.
        CurrentStatusLabel = () => CoordinationCard.EffectiveStatusLabel(true),
        StatusExplanatoryEvent = S(() => {
            // Find the event that best explains the current status. Could be one of several event types.
            let bestGuess = CoordinationCard.Self()?.Events()
                .filter(ev =>
                    // Status update events can clearly explain the current status
                    ev.Type() === EventType.CCardStatusUpdate
                    // If the status has never changed since the last (re)requested event, then that might
                    // explain the current status.
                 || ev.Type() === EventType.CCardRequested
                )
                .reduce((currentGuess, nextEv) => currentGuess === undefined || nextEv.Id() > currentGuess.Id() ? nextEv : currentGuess, undefined as Event | undefined);

            // If best guess so far is a status update event, see if we can improve our guess.
            if (bestGuess?.Type() === EventType.CCardStatusUpdate)  bestGuess = bestGuess.CCardStatusUpdateCause();

            // Finally, to defend against holes in our event history, only use our guess should its StatusId
            // match the current StatusId. Otherwise, in case of a mismatch, give priority to the current
            // StatusId.
            return bestGuess?.CoordinationCardVersion()?.StatusId() === CoordinationCard.StatusId() ? bestGuess : null;
        }),
        // clock for time ago language in detailed status explanation text
        statusExplanationClock = tzutils.clock(1000),
        StatusExplanationNow = () => statusExplanationClock.now(),
        StatusReasonExplanatoryEvent = S(() => {
            // StatusReason gets set at the same time as Status. So the event that explains the current status
            // SHOULD also explain the current StatusReason. Just do a defensive check to make sure the event's
            // StatusReason matches the current StatusReason, and again we'll give priority to the current
            // StatusReason in case of a mismatch.
            const ev = StatusExplanatoryEvent();
            return ev?.CoordinationCardVersion()?.StatusReason() === CoordinationCard.StatusReason() ? ev : null;
        }),
        AvailableStatuses =
            lift(() => HasEditStatus() ? Array.from(data.CoordinationCardStatuses.items()) : [])
                // Pending is never available as an option for admins to choose (it can only be the
                // result of an auto status reset).
                .filter(s => s.Id() !== CoordinationCardStatuses.Pending)
                .map(s => AvailableStatus(CoordinationCard, s, () => pendingStatus(s)))
                .orderBy(s => s.Model.DisplayOrder()),
        pendingStatus = S.value<CoordinationCardStatus | null>(null),
        pendingStatusUpdate = S(() =>
            pendingStatus() === null ? null : PendingStatusUpdate(
                CoordinationCard,
                pendingStatus()!,
                ajax,
                entrypoints,
                DismissDetails,
                () => pendingStatus(null)
            )
        ),
        Loading = () => pendingStatusUpdate()?.Submitting() ?? false;

    return {
        CoordinationCard,
        CurrentStatusLabel,
        StatusExplanatoryEvent,
        StatusExplanationNow,
        StatusReasonExplanatoryEvent,
        AvailableStatuses,
        PendingStatusUpdate: pendingStatusUpdate,
        Loading
    };
}

export type AvailableStatus = ReturnType<typeof AvailableStatus>;
function AvailableStatus(
    ccard : CoordinationCard,
    status : CoordinationCardStatus,
    Select : () => void
) {
    const Label = () => ccard.Coordination()?.EffectiveStatusLabel(status) ?? '';

    return {
        Model: status,
        Label,
        Select
    };
}

export type PendingStatusUpdate = ReturnType<typeof PendingStatusUpdate>;
function PendingStatusUpdate(
    ccard : CoordinationCard,
    status : CoordinationCardStatus,
    ajax : IAjax,
    entrypoints : Entrypoints,
    dismissDetails : () => void,
    dismissPendingUpdate : () => void
) {
    const
        url = entrypoints.CoordinationCardStatusUpdates({ Id: ccard.Id() }),
        client = ajax.Url<CoordinationCardStatusUpdateRequest, unknown>(url, { useCache: true }),
        Request = new CoordinationCardStatusUpdateRequest({
            StatusId: status.Id(),
            // When reconfirming the existing status, default to the existing status reason, if any, otherwise
            // start with an empty status reason.
            StatusReason: S.sample(() => ccard.StatusId() === status.Id() ? ccard.StatusReason() : null)
        }),
        StatusLabel = () => ccard.Coordination()?.EffectiveStatusLabel(status) ?? '',
        IsStatusReasonRequired = () => status.Id() === CoordinationCardStatuses.Denied,
        CanSubmit = () => !!Request.StatusReason() || !IsStatusReasonRequired(),
        Submitting = S.value(false);

    return {
        Request,
        StatusLabel,
        IsStatusReasonRequired,
        CanSubmit,
        Submit,
        Submitting,
        Cancel: dismissPendingUpdate
    };

    function Submit() {
        if (Submitting()) return;
        Submitting(true);
        client.Post(Request)
            .then(dismissDetails)
            .finally(() => Submitting(false));
    }
}



// WEBPACK FOOTER //
// ./apps/portcall/coordinationcarddetails/status.ts